import { FC, useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { PercentageLineChart } from '@/_shared/components/PercentageLineChart';
import { Tooltip } from '@/_shared/components/Tooltip';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentTeamCreditsCall } from '../../../state';

type Props = {
  teamName?: string;
};

export const DivisionByTrendTeam: FC<Props> = () => {
  const t = useTranslations('departments.tabs.credits.trend');
  const tShared = useTranslations();

  const { genericResponsiveValue } = useResponsiveStyle();

  const { stats } = getDepartmentTeamCreditsCall.hook();

  const data = useMemo(() => {
    return Object.keys(stats.monthlyUsage).map(key => {
      const monthName = `${tShared(
        `months.${dayjs(key).format('MMMM').toLowerCase()}`,
      )} ${dayjs(key).format('YYYY')}`;
      return { key: monthName, value: stats.monthlyUsage[key] };
    });
  }, [stats.monthlyUsage, tShared]);

  const monthsList = useMemo(() => {
    const keys = Object.keys(stats.monthlyUsage);
    if (keys.length === 0) return '';
    const first = `${tShared(`months.${dayjs(keys[0]).format('MMMM').toLowerCase()}`)} ${dayjs(keys[0]).format('YYYY')}`;
    const last = `${tShared(
      `months.${dayjs(keys[keys.length - 1])
        .format('MMMM')
        .toLowerCase()}`,
    )} ${dayjs(keys[keys.length - 1]).format('YYYY')}`;
    return keys.length === 1 ? first : `${first} - ${last}`;
  }, [stats.monthlyUsage, tShared]);

  return (
    <Box mt='m'>
      <Typography mt='xl' variant='h3'>
        {t('title')}
      </Typography>
      <Typography variant='p3/light'>{t('descriptionTeam')}</Typography>
      <Box
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: { desktop: '1fr 1fr' },
          defaultValue: '1fr',
        })}
        gridColumnGap='s'
        mt='s'
      >
        <Card
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 's',
          })}
        >
          <Typography variant='h4' mb='s'>
            {t('chartTitle')}
          </Typography>
          <PercentageLineChart data={data} />
        </Card>
        <Box>
          <Card>
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Typography mr='xxs' variant='h3'>
                {t('averageMonthlyUsage')}
              </Typography>
              <Tooltip iconProps={{ size: 'xs' }} iconName='info'>
                {t('averageMonthlyUsageTooltip')}
              </Tooltip>
            </Box>
            <Typography variant='p2/light'>{monthsList}</Typography>
            <Typography mt='s' variant='special'>
              {stats.avgUsedPerPerson}{' '}
              <Typography as='span' variant='p2/light'>
                {t('creditLabel')}
              </Typography>
            </Typography>
          </Card>
          <Card mt='s'>
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Typography mr='xxs' variant='h3'>
                {t('trendLabel')}
              </Typography>
              <Tooltip iconProps={{ size: 'xs' }} iconName='info'>
                {t('trendLabelTooltip')}
              </Tooltip>
            </Box>
            <Typography variant='p2/light'>{monthsList}</Typography>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              flexDirection='row'
              mt='s'
            >
              <Typography variant='special'>
                {stats?.deltaUsedPercentage}%{' '}
                {stats &&
                  stats?.deltaUsedPercentage !== undefined &&
                  stats?.deltaUsedPercentage > 0 && (
                    <Typography
                      as='span'
                      color={
                        stats &&
                        stats?.deltaUsedPercentage !== undefined &&
                        stats.deltaUsedPercentage >= 0
                          ? 'success'
                          : 'error'
                      }
                      variant='p2/light'
                    >
                      {stats &&
                      stats?.deltaUsedPercentage !== undefined &&
                      stats.deltaUsedPercentage >= 0
                        ? '\u2191'
                        : '\u2193'}
                    </Typography>
                  )}
              </Typography>
              {stats && stats?.deltaUsed !== undefined && stats?.deltaUsed > 0 && (
                <Box px='xxs' display='inline-block' borderRadius='xxl' backgroundColor='primary'>
                  <Typography variant='p2' fontSize='xs'>
                    {stats.deltaUsed} {t('creditsLabel')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
